/**
 * Paths that render for somebody who is not signed in.
 *
 * ⚠️ THERE IS ONE, AND IT IS MATCHED OUTSIDE THE ROUTER. `<BrowserRouter>`
 * lives inside `AuthenticatedShell`, so anything added to it is reachable only
 * by a signed-in user; a signed-out visitor falls through to
 * `<UnauthenticatedTemplate>` and gets the sign-in screen. For a link texted to
 * somebody who has never opened the Project Tracker, that is the one screen it
 * must never show — so `App()` tests this before either template.
 *
 * It lives in its own module so the pattern can be imported by the test that
 * pins its behaviour, rather than scraped out of `App.tsx` by a regex. Pulling
 * `App.tsx` itself into a node-environment test would drag in MSAL and every
 * route module with it.
 */

/**
 * `/travel-acknowledgment/<anything>`, with an optional trailing slash.
 *
 * ⚠️ ANCHORED AT BOTH ENDS, and both anchors are load-bearing. Without `^` a
 * crafted path like `/projects/1/travel-acknowledgment/<token>` would render
 * the public page in place of the app; without `$` anything could follow.
 *
 * ⚠️⚠️ IT DELIBERATELY DOES NOT CHECK THE TOKEN'S SHAPE, AND THE FIRST VERSION
 * DID — `([A-Za-z0-9_-]{43})`, the exact base64url shape the API requires.
 * That looked like defence in depth and was the opposite.
 *
 * A link one character short, or with a trailing `.` that an SMS client stuck
 * to it, failed to match — so the page never mounted, the app shell took the
 * path instead, and the signed-out traveller was shown the Entra SIGN-IN
 * SCREEN. The one outcome this whole arrangement exists to prevent, reached by
 * the most ordinary accident there is.
 *
 * It also made the translated "this link is not valid" copy unreachable for
 * precisely the case it was written for: a malformed token never got as far as
 * the page that explains it.
 *
 * So the route matches on the PATH and the page judges the token — which it
 * can do properly, because the API shape-checks it and 404s, and a 404 is what
 * renders that message. Nothing is weakened: the page shows nothing at all
 * without a record the API agreed to return.
 */
export const ACK_PATH = /^\/travel-acknowledgment\/([^/?#]+)\/?$/;

/**
 * The token in an acknowledgment path, or null when the path is not one.
 *
 * ⚠️ IT TRIMS TRAILING CHARACTERS THAT CANNOT BE IN A TOKEN. The alphabet is
 * base64url — `A-Z a-z 0-9 _ -` and nothing else — so a trailing `.`, `,`, `)`
 * or `"` was never part of the token and can only have been added in transit.
 * Messaging apps append exactly those when a sender writes "here it is: <link>."
 * and the client's link detector swallows the full stop.
 *
 * ⚠️ AND IT TRIMS THEM PERCENT-ENCODED, because that is how most of them
 * arrive. `location.pathname` is the ENCODED path: a trailing `"` reaches this
 * function as `%22`, and the curly quotes a phone types for you as `%E2%80%9D`
 * or `%E2%80%99` — every one ending in characters that ARE in the alphabet, so
 * a trim on single characters left them attached and the link failed. (Codex,
 * P2 on #542; measured in Chromium.) So an escape is trimmed as a unit.
 *
 * It matches the escape rather than decoding it: `decodeURIComponent` throws
 * on a truncated escape, and there is nothing a decode would recover that the
 * trim needs — the token itself is never encoded.
 *
 * Trimming is safe in a way that guessing usually is not: neither `%` nor any
 * character outside the alphabet can be one the generator produced, so this
 * can rescue a broken link and cannot corrupt a good one.
 *
 * It never returns null for a path that matched. A segment that trims away to
 * nothing still renders the page, which says the link is not valid — far
 * better than falling through to a sign-in screen.
 */
export function acknowledgmentTokenFrom(pathname: string): string | null {
  const raw = ACK_PATH.exec(pathname)?.[1];
  if (raw == null) return null;
  return raw.replace(/(?:%[0-9A-Fa-f]{0,2}|[^A-Za-z0-9_-])+$/, '') || raw;
}
